import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

import type { AiFeature } from "@intelligems/ig-types";

import type { WorkspaceMonacoConfig } from "./monaco/workspaceMonacoModels";
import type {
  PublishBlockReason,
  WorkspaceDependency,
  WorkspaceGateResult,
} from "./types";

// ---------------------------------------------------------------------------
// WorkspaceDomainConfig — everything domain-specific the shell needs, as data.
//
// The shell holds no `if (domain === ...)` branch: a domain is exactly this
// object plus a WorkspaceApiPort. Anything the shell would otherwise have to
// know about shipping providers or recommendation rules lives here.
//
// The config is *built* rather than declared, because the tab renderers and the
// publish-review rows legitimately close over live view-model state. The parts
// that are genuinely static (gate order, filenames, monaco URIs) are exported as
// module constants by each binding so a drift alarm can assert them directly
// without constructing a view model.
// ---------------------------------------------------------------------------

/** One gate row's presentation. */
export interface WorkspaceGateSpec<GateKey extends string> {
  key: GateKey;
  /** Pill label in the rail — must stay short enough not to wrap. */
  shortLabel: string;
  /** Full name shown in the gates-tab row header. */
  fullLabel: string;
  /**
   * Tooltip title in the gate rail, when it differs from {@link fullLabel} —
   * the rail has less room than the tab, so several shipping gates read
   * shorter there. Falls back to `fullLabel`.
   */
  railLabel?: string;
  /**
   * Renders this gate's `detailsJson` into the expanded row body. Replaces the
   * per-gate ternary chain the shipping tab used to carry inline. Returning
   * `null` falls back to the gate's plain `result` string. The whole gate is
   * passed too, since some copy depends on the status ("passed" vs "ran").
   */
  formatDetails?: (
    details: Record<string, unknown>,
    gate: WorkspaceGateResult<GateKey>
  ) => string | null;
}

/** One predefined check inside the required-checks gate. */
export interface WorkspaceRequiredCheckSpec {
  key: string;
  label: string;
  /** Actionable remediation shown when the check fails. */
  hint: string;
}

export interface WorkspaceRequiredChecksConfig<GateKey extends string> {
  /** The gate whose `detailsJson.failing` / `.results` drive the check states. */
  gateKey: GateKey;
  /**
   * A check whose verdict comes from a *different* gate. Shipping's
   * `runtime_under_budget` is enforced by `runtime_timeout`, not by the
   * required-tests gate, so a runtime overrun must show Fail even while the
   * required gate is green.
   */
  externallyGatedCheck: { checkKey: string; gateKey: GateKey } | null;
  checks: readonly WorkspaceRequiredCheckSpec[];
}

export interface WorkspaceGatesConfig<GateKey extends string> {
  /** Canonical order. Must mirror the domain's own gate-order constant. */
  order: readonly WorkspaceGateSpec<GateKey>[];
  /**
   * Gates that run in the publish job's DEPLOY phase, after every validation
   * gate has already passed. They are the publish attempt's own outcome, not a
   * precondition for it: a failed deploy is fixed by publishing again, so these
   * gates never contribute to the publish block. Dry runs skip them entirely.
   */
  deployGates: readonly GateKey[];
  /** The gate that flips to `running` the moment a publish/dry-run is kicked off. */
  contractGate: GateKey;
  /** The three gates whose `detailsJson.consoleLogs` feed the editor console. */
  consoleGates: {
    required: GateKey;
    generatedSuite: GateKey;
    userSuite: GateKey;
  };
  /** The gate carrying the freshly generated suite in `detailsJson.testSuite`. */
  generatedSuiteGate: GateKey;
  /**
   * A `passed` gate whose `result` is this sentinel is presented as an
   * informational warning rather than a green pass — "passed because there was
   * nothing to run" is not the same claim as "passed".
   */
  noUserTestsSentinel: { gateKey: GateKey; result: string } | null;
  /** `null` for domains with no predefined-check breakdown. */
  requiredChecks: WorkspaceRequiredChecksConfig<GateKey> | null;
  /**
   * The suite runner's own describe prefix, stripped from failed-test names so
   * a gate row shows the test the user actually wrote. Capture group 1 is the
   * remaining name.
   */
  testFailureTitlePrefix?: RegExp;
}

/**
 * A bottom tab. `unavailable` is a distinct variant rather than an optional
 * `render`, so a tab can never be declared present and then silently render an
 * empty pane — the type forces either a renderer or a reason.
 */
export type WorkspaceTabAvailability =
  | { kind: "enabled"; render: () => ReactNode; badge?: number }
  | { kind: "unavailable"; reason: string };

export interface WorkspaceTabSpec<TabKey extends string> {
  key: TabKey;
  label: string;
  availability: WorkspaceTabAvailability;
}

/** Chrome copy and iconography. */
export interface WorkspaceChromeConfig {
  Icon: LucideIcon;
  /** Breadcrumb trail, most general first. */
  breadcrumbs: readonly string[];
  /** Title fallback when the entity has no name yet. */
  untitledLabel: string;
  /**
   * Display name for the domain's AI authoring assistant (e.g. "Atlas"),
   * rendered as the author byline on chat-origin versions in version history.
   */
  aiAuthorName: string;
  /**
   * Accessible name for the gate-checks region, e.g. "Publish gates". It is
   * the region's label rather than visible text: the strip leads with what the
   * run is doing, and a static heading in front of that is noise.
   */
  gatesHeading: string;
  /**
   * Who else in the org can read an org secret, in this domain's words (e.g.
   * "Every org secret is available to all custom shipping providers for this
   * organization."). Rendered in the secrets modal beneath the never-shown
   * notice; see {@link SecretsManagementModalProps.scopeMessage}.
   */
  secretsScopeMessage: string;
  /**
   * `window.confirm` copy for unpublishing the active version. Optional
   * because `unpublish` is an optional port capability group — a domain that
   * does not supply the group can never reach this prompt, and requiring the
   * string would make it author copy for a verb it cannot perform.
   */
  confirmUnpublish?: string;
  /**
   * `window.confirm` copy for importing a version over a dirty draft. Optional
   * for the same reason as {@link confirmUnpublish}: it belongs to the
   * optional `versionImport` capability group.
   */
  confirmDirtyImport?: string;
}

export interface WorkspacePublishBlockCopy {
  title: string;
  message: string;
  tooltip: string;
}

export interface WorkspacePublishReviewConfig<GateKey extends string> {
  /** Named per domain because the copy says "provider" / "rule". */
  blockCopy: Record<PublishBlockReason, WorkspacePublishBlockCopy>;
  /** Domain rows rendered between the version row and the notes field. */
  extraRows?: ReactNode;
  /** Gate pass/fail rows summarizing what the publish will ship. */
  gateSummaryRows: readonly { label: string; gateKey: GateKey }[];
  /** Trailing sentence explaining what publishing actually changes. */
  impactCopy: (versionToPublish: number) => string;
}

/**
 * One additional locked, read-only tab beyond the contract types module — e.g.
 * Agentic Logic's `sdk.d.ts`. Mirrors the corresponding entry the domain also
 * registers on {@link WorkspaceMonacoConfig.readonlyTabs} for the Monaco model
 * graph; kept here too so the brain can resolve the tab's filename and content
 * without depending on the Monaco config.
 */
export interface WorkspaceReadonlyFile {
  filename: string;
  source: string;
}

/** Filenames, codec, and starter content for the editable file set. */
export interface WorkspaceFilesConfig {
  entryFilename: string;
  /** The locked contract tab's filename. */
  typesFilename: string;
  /** Names the save-path schema refuses, so the editor rejects them up front. */
  reservedFilenames: readonly string[];
  /** Content for a freshly-added helper file. */
  newFileTemplate: string;
  /** Base name for generated helper files (`helper` → `helper.ts`, `helper-2.ts`). */
  newFileBasename: string;
  /**
   * `false` for single-file domains, which persist `source` as one file's
   * contents rather than a serialized manifest and therefore hide
   * add/rename/delete entirely.
   */
  allowUserFiles: boolean;
  /** Content of the locked contract tab. */
  typesModuleSource: string;
  /**
   * Additional locked, read-only tabs shown after the contract types tab.
   * Empty for domains with no further environment-injected modules to show.
   */
  readonlyFiles: readonly WorkspaceReadonlyFile[];
  /** Seed content for a brand-new entity's entry file. */
  starterSource: string;
  /**
   * Parses persisted draft bytes into the editable file set. The inverse lives
   * in the API port, because each domain's save endpoint takes a different body
   * shape (a structured manifest for CSP, an inline string for single-file
   * domains) rather than an opaque encoded blob.
   */
  decode: (raw: string) => Record<string, string>;
}

/** The two test files surfaced in the publish-gates tab. */
export interface WorkspaceTestFilesConfig {
  generatedFilename: string;
  userFilename: string;
}

/**
 * The test suites as files in the editor's file tree, below the rule files.
 * `null` on {@link WorkspaceBrainConfig.editorTests} for a domain whose suites
 * live elsewhere (shipping keeps them in its Publish gates tab).
 */
export interface WorkspaceEditorTestsConfig {
  files: WorkspaceTestFilesConfig;
  /** What "Add test" seeds into an empty user suite. */
  userSuiteSkeleton: string;
}

/** AI chat wiring, or `null` for domains with no authoring assistant. */
export interface WorkspaceAiChatConfig {
  workflowId: string;
  feature: AiFeature;
  /** The `ui-action` name whose payload carries generated source. */
  uiActionName: string;
  /** Domain context merged into every chat turn's variables. */
  extraVariables?: Record<string, unknown>;
  /**
   * Unwraps the generated payload into editor-ready source. Multi-file domains
   * hand back a serialized manifest here, so applying it verbatim would drop a
   * JSON blob into the editor. Defaults to identity.
   */
  decodeGeneratedSource?: (raw: string) => string;
}

/**
 * Whether a domain lets an author declare npm dependencies for the code it
 * bundles.
 *
 * `unsupported` is a distinct variant rather than a boolean, for the same
 * reason {@link WorkspaceTabAvailability} is: a domain that admits no package
 * owes the author the reason, and the panel shows it in place of the add
 * control. Offering the control anyway is not a cosmetic slip — the API
 * refuses such a dependency, so the form would collect an entry that can only
 * fail later.
 */
export type WorkspaceDependencyManagement =
  | { kind: "enabled" }
  | { kind: "unsupported"; reason: string };

export interface WorkspaceControlsPanelConfig {
  /** Static runtime facts (engine, timeout, fallback …) shown in the panel. */
  runtimeFacts: readonly { label: string; value: string; mono?: boolean }[];
  /** Prose describing the runtime contract; forwarded to the AI chat. */
  runtimeContract: string;
  /** Whether this domain's authors may declare npm dependencies at all. */
  dependencies: WorkspaceDependencyManagement;
}

/**
 * The subset the shared brain needs. Split out because the brain runs inside the
 * binding hook, which cannot build tab renderers — those close over the very
 * view model the brain is producing.
 */
export interface WorkspaceBrainConfig<GateKey extends string> {
  gates: WorkspaceGatesConfig<GateKey>;
  files: WorkspaceFilesConfig;
  /**
   * Opt-in: the suites as editor files. Needs the port's `suiteInventory` as
   * well; with either one `null` the file tree shows no tests.
   */
  editorTests: WorkspaceEditorTestsConfig | null;
  /** Only the confirm copy; the brain renders no chrome. */
  chrome: Pick<
    WorkspaceChromeConfig,
    "confirmUnpublish" | "confirmDirtyImport"
  >;
  /**
   * localStorage namespace for the pre-persist generated-suite cache, read
   * only by a domain whose suites are NOT editor files (`editorTests` null):
   * the presigned-URL + localStorage path is how such a domain's suite
   * survives a reload. A domain with editor test files carries its suite on
   * the draft and inventories it server-side, so it has no cache and omits
   * this. Must stay stable per domain — changing it orphans every user's
   * cached suite.
   */
  generatedTestsCachePrefix?: string;
}

export interface WorkspaceDomainConfig<
  GateKey extends string,
  TabKey extends string,
> extends WorkspaceBrainConfig<GateKey> {
  chrome: WorkspaceChromeConfig;
  tabs: readonly WorkspaceTabSpec<TabKey>[];
  testFiles: WorkspaceTestFilesConfig;
  monaco: WorkspaceMonacoConfig;
  aiChat: WorkspaceAiChatConfig | null;
  controlsPanel: WorkspaceControlsPanelConfig;
  publishReview: WorkspacePublishReviewConfig<GateKey>;
}

/** The first tab that is not `unavailable`, or the first tab if all are. */
export const defaultTabKey = <TabKey extends string>(
  tabs: readonly WorkspaceTabSpec<TabKey>[]
): TabKey => {
  const enabled = tabs.find((tab) => tab.availability.kind === "enabled");
  return (enabled ?? tabs[0]).key;
};

/** Looks up a gate's presentation, falling back to a humanized key. */
export const gateSpec = <GateKey extends string>(
  gates: WorkspaceGatesConfig<GateKey>,
  key: GateKey
): WorkspaceGateSpec<GateKey> =>
  gates.order.find((spec) => spec.key === key) ?? {
    key,
    shortLabel: key,
    fullLabel: key,
  };

export type { WorkspaceDependency };
